import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Link, 
  InputAdornment, 
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  Visibility as VisibilityIcon, 
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';



const Signup: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Validation state
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [termsError, setTermsError] = useState('');
  
  // Validation functions
  const validateFirstName = (value: string) => {
    if (!value.trim()) {
      setFirstNameError('First name is required');
      return false;
    } else {
      setFirstNameError('');
      return true;
    }
  };
  
  const validateLastName = (value: string) => {
    if (!value.trim()) {
      setLastNameError('Last name is required');
      return false;
    } else {
      setLastNameError('');
      return true;
    }
  };
  
  const validateEmail = (value: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!value) {
      setEmailError('Email is required');
      return false;
    } else if (!re.test(value.toLowerCase())) {
      setEmailError('Invalid email address');
      return false;
    } else {
      setEmailError('');
      return true;
    }
  };
  
  const validatePassword = (value: string) => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    } else if (value.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    } else if (!/(?=.*[0-9])/.test(value)) {
      setPasswordError('Password must contain at least one number');
      return false;
    } else if (!/(?=.*[a-z])/.test(value)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return false;
    } else if (!/(?=.*[A-Z])/.test(value)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return false;
    } else if (!/(?=.*[!@#$%^&*])/.test(value)) {
      setPasswordError('Password must contain at least one special character');
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };
  
  const validateConfirmPassword = (value: string) => {
    if (!value) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    } else if (value !== password) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    } else {
      setConfirmPasswordError('');
      return true;
    }
  };
  
  const validateCompanyName = (value: string) => {
    if (!value.trim()) {
      setCompanyNameError('Company name is required');
      return false;
    } else {
      setCompanyNameError('');
      return true;
    }
  };
  
  const validatePhoneNumber = (value: string) => {
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (!value) {
      setPhoneNumberError('Phone number is required');
      return false;
    } else if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
      setPhoneNumberError('Invalid phone number');
      return false;
    } else {
      setPhoneNumberError('');
      return true;
    }
  };
  
  const validateTerms = (checked: boolean) => {
    if (!checked) {
      setTermsError('You must accept the terms and conditions');
      return false;
    } else {
      setTermsError('');
      return true;
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    const isCompanyNameValid = validateCompanyName(companyName);
    const isPhoneNumberValid = validatePhoneNumber(phoneNumber);
    const isTermsAccepted = validateTerms(acceptTerms);
    
    if (!isFirstNameValid || !isLastNameValid || !isEmailValid || 
        !isPasswordValid || !isConfirmPasswordValid || 
        !isCompanyNameValid || !isPhoneNumberValid || !isTermsAccepted) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Redirect to login page after successful registration
      navigate('/login', { state: { registerSuccess: true } });
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSocialSignup = (provider: string) => {
    setLoading(true);
    // Simulate API delay
    setTimeout(() => {
      setLoading(false);
      navigate('/');
    }, 1500);
  };
  
  return (
    <Box 
      sx={{
        height: '100vh',
        display: 'flex',
        overflow: 'hidden'
      }}
    >
      {/* Left side - Image */}
      {!isMobile && (
        <Box 
          sx={{ 
            flex: '1 1 50%',
            position: 'relative',
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'url(https://images.unsplash.com/photo-1601628828688-632f38a5a7d0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1776&q=80)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 4,
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 2 }}>
              Start Your Journey
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, maxWidth: '80%' }}>
              Join thousands of fleet managers who trust our platform
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                maxWidth: '400px',
                width: '100%',
                mt: 4,
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Why Choose Us?
              </Typography>
              <Typography variant="body1">
                • Intuitive and user-friendly interface
              </Typography>
              <Typography variant="body1">
                • Real-time tracking and alerts
              </Typography>
              <Typography variant="body1">
                • Comprehensive reporting tools
              </Typography>
              <Typography variant="body1">
                • 24/7 customer support
              </Typography>
              <Typography variant="body1">
                • Secure and reliable platform
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Right side - Form */}
      <Box 
        sx={{ 
          flex: { xs: '1 1 100%', md: '1 1 50%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflowY: 'auto',
          bgcolor: 'background.default',
          p: { xs: 2, sm: 4 }
        }}
      >
        <Container maxWidth="sm">
          <Paper 
            elevation={isMobile ? 3 : 0} 
            sx={{ 
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}
          >
            
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              Create an Account
            </Typography>
            
            <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 4 }}>
              Join the Fleet Management System
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box>
                  <TextField
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => validateFirstName(firstName)}
                    error={!!firstNameError}
                    helperText={firstNameError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => validateLastName(lastName)}
                    error={!!lastNameError}
                    helperText={lastNameError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => validateEmail(email)}
                    error={!!emailError}
                    helperText={emailError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    required
                    fullWidth
                    name="companyName"
                    label="Company Name"
                    id="companyName"
                    autoComplete="organization"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    onBlur={() => validateCompanyName(companyName)}
                    error={!!companyNameError}
                    helperText={companyNameError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    required
                    fullWidth
                    name="phoneNumber"
                    label="Phone Number"
                    id="phoneNumber"
                    autoComplete="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onBlur={() => validatePhoneNumber(phoneNumber)}
                    error={!!phoneNumberError}
                    helperText={phoneNumberError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => validatePassword(password)}
                    error={!!passwordError}
                    helperText={passwordError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => validateConfirmPassword(confirmPassword)}
                    error={!!confirmPasswordError}
                    helperText={confirmPasswordError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={acceptTerms}
                        onChange={(e) => {
                          setAcceptTerms(e.target.checked);
                          validateTerms(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label="I accept the terms and conditions"
                  />
                  {termsError && (
                    <Typography variant="caption" color="error">
                      {termsError}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign Up'}
              </Button>
              
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
              

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2">
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/login" variant="body2">
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Signup; 